/**
 * Toast Message Constants
 *
 * Centralized location for all toast notification messages.
 * This ensures consistency across the application and makes it easy
 * to update messaging globally.
 */

export const ToastMessages = {
  // Linked Table Messages
  LINKED_TABLE: {
    CREATED: 'Linked table created',
    CREATED_FAILED: (msg?: string) => msg ?? 'Failed to create linked table',
    SAVED: 'Linked table saved',
    SAVED_FAILED: (msg?: string) => msg ?? 'Failed to save linked table',
    NAME_REQUIRED: 'Name required',
    NAME_REQUIRED_DESC: 'Please enter a linked table name',
    DELETED: (name: string) => `"${name}" deleted`,
    DELETE_FAILED: (msg?: string) => msg ?? 'Failed to delete linked table',
    DELETE_BLOCKED: 'Cannot delete linked table',
    DELETE_BLOCKED_DESC: (linkedTables: string[]) =>
      `Used in ${linkedTables.length} linked table(s): ${linkedTables.join(', ')}`,
    PREVIEW_FAILED: 'Failed to preview changes',
    CHANGES_REVERTED: 'All changes have been reverted',
    RELATIONSHIP_REMOVED: 'Relationship removed',
    RELATIONSHIP_REMOVED_WITH_DETAILS: (details: string[]) =>
      `Removed relationship and cleaned up: ${details.join(', ')}`,
  },

  // Schema Messages
  SCHEMA: {
    FIELD_DELETED: 'Field deleted',
    FIELD_DELETE_FAILED: (msg?: string) => msg ?? 'Failed to delete field',
    FIELD_DELETE_BLOCKED: 'Cannot delete field',
    FIELD_DELETE_BLOCKED_DESC: (linkedTables: string[]) =>
      `Used in ${linkedTables.length} linked table(s): ${linkedTables.join(', ')}`,
    VALIDATION_FAILED: (error?: string) => error ?? 'Validation failed',
    SAVED: 'Schema saved',
    SAVE_FAILED: (error?: string) => error ?? 'Failed to save schema',
  },

  // Table Messages
  TABLE: {
    CREATED: (name: string) => `"${name}" created`,
    SAVED: 'Table saved',
    DELETED: (name: string) => `"${name}" deleted`,
    ERROR: (msg: string) => msg,
    NAME_REQUIRED: 'Name required',
    NAME_REQUIRED_DESC: 'Please enter a table name',
    DESCRIPTION_REQUIRED: 'Description required',
    DESCRIPTION_REQUIRED_DESC: 'Please enter a table description',
    UPDATED: 'Table updated',
    UPDATE_FAILED: (msg?: string) => msg ?? 'Failed to update table',
    DELETE_BLOCKED: 'Cannot delete table',
    DELETE_BLOCKED_DESC: (linkedTables: string[]) =>
      `Used in ${linkedTables.length} linked table(s): ${linkedTables.join(', ')}`,
    DELETE_FAILED: (msg?: string) => msg ?? 'Failed to delete table',
    DRAFT_DELETED: 'Draft table deleted',
    STATUS_UPDATED: 'Status updated',
    STATUS_UPDATE_FAILED: 'Failed to update status',
  },

  // Version Messages
  VERSION: {
    CREATED: 'Version created',
    CREATED_DESC: 'Processing version...',
    DELETED: 'Version deleted',
    DELETED_DESC: (fileName: string) => `"${fileName}" has been deleted`,
    DELETE_FAILED: (msg?: string) => msg ?? 'Failed to delete version',
    DELETE_BLOCKED: 'Cannot delete version',
    DELETE_BLOCKED_DESC: (linkedTables: string[]) =>
      `Used in ${linkedTables.length} linked table(s): ${linkedTables.join(', ')}`,
    DELETE_PROTECTED: 'Cannot delete version',
    DELETE_PROTECTED_DESC:
      'This version is tagged as Current or Previous. Clear the system tag first.',
    DELETE_LAST: 'Cannot delete version',
    DELETE_LAST_DESC: 'You must keep at least one version.',
    ERROR: (msg: string) => msg,
    VALIDATION_ERROR: (msg?: string) => msg ?? 'Validation error',
    PROCESSING_FAILED: (msg: string) => `Version failed: ${msg}`,
    // System tag operations
    SET_CURRENT: 'Set as Current',
    SET_CURRENT_DESC: 'Version rotation applied',
    SET_CURRENT_FAILED: 'Failed to set as Current',
    SET_PREVIOUS: 'Set as Previous',
    SET_PREVIOUS_DESC: 'Version updated',
    SET_PREVIOUS_FAILED: 'Failed to set as Previous',
    ALREADY_PREVIOUS: 'Already tagged as Previous',
    TAG_CLEARED: 'System tag cleared',
    TAG_CLEARED_DESC: 'Version is no longer tagged',
    TAG_CLEAR_FAILED: 'Failed to clear system tag',
    // Export
    EXPORT_STARTED: 'Export started',
    EXPORT_STARTED_DESC: (format: string) => `Downloading as ${format.toUpperCase()}`,
    EXPORT_FAILED: (msg?: string) => msg ?? 'Export failed',
  },

  // Folder Messages
  FOLDER: {
    CREATED: 'Folder created',
    CREATE_FAILED: 'Failed to create folder',
    UPDATED: 'Folder updated',
    UPDATE_FAILED: 'Failed to update folder',
    RENAMED: 'Folder renamed',
    RENAME_FAILED: 'Failed to rename folder',
    DELETED: 'Folder moved to trash',
    DELETE_FAILED: 'Failed to delete folder',
    RESTORED: 'Folder restored',
    RESTORE_FAILED: 'Failed to restore folder',
    MOVED: (folderName: string) => `Moved to "${folderName}"`,
    MOVED_TO_ROOT: 'Moved to root',
    MOVE_FAILED: 'Failed to move folder',
    NAME_CONFLICT: (name: string) => `A folder named "${name}" already exists here`,
    CANNOT_MOVE_INTO_SELF: 'Cannot move a folder into itself',
    CANNOT_MOVE_INTO_CHILD: 'Cannot move a folder into its own subfolder',
    PARENT_NOT_FOUND: 'The target folder no longer exists',
    EMPTY_NAME: 'Folder name cannot be empty',
  },

  // Workspace Item Messages (tables, linked tables, folders)
  WORKSPACE: {
    // Trash operations
    ITEM_TRASHED: 'Moved to trash',
    ITEMS_TRASHED: (count: number) => `${count} items moved to trash`,
    ITEM_RESTORED: 'Restored from trash',
    ITEMS_RESTORED: (count: number) => `${count} items restored`,
    RESTORE_FAILED: 'Failed to restore item',
    TRASH_FAILED: 'Failed to move to trash',
    TRASH_EMPTIED: 'Trash emptied',
    TRASH_EMPTY_FAILED: 'Failed to empty trash',
    PERMANENT_DELETE: 'Permanently deleted',
    PERMANENT_DELETE_FAILED: 'Failed to permanently delete',

    // Move operations
    MOVE_SUCCESS: (folderName: string) => `Moved to "${folderName}"`,
    MOVE_TO_ROOT: 'Moved to root',
    MOVE_FAILED: 'Failed to move item',
    BULK_MOVE_SUCCESS: (count: number, folderName: string) =>
      `${count} items moved to "${folderName}"`,
    BULK_MOVE_TO_ROOT: (count: number) => `${count} items moved to root`,
    UNDO_MOVE_FAILED: 'Failed to undo move',

    // Favorite operations
    FAVORITED: 'Added to favorites',
    UNFAVORITED: 'Removed from favorites',
    FAVORITE_FAILED: 'Failed to update favorite',

    // Rename operations
    RENAMED: 'Renamed successfully',
    RENAME_FAILED: 'Failed to rename',
    NAME_CONFLICT: (name: string, type: string) =>
      `A ${type} named "${name}" already exists here`,
    EMPTY_NAME: 'Name cannot be empty',

    // Duplicate operations
    DUPLICATED: (name: string) => `"${name}" duplicated`,
    DUPLICATE_FAILED: (msg?: string) => msg ?? 'Failed to duplicate',
    DUPLICATE_NOT_SUPPORTED: (type: string) => `${type}s cannot be duplicated`,
    DUPLICATE_TABLE_HINT: 'Use the version system to create new versions',
    DUPLICATE_FOLDER_HINT: 'Create a new folder instead',

    // Clipboard operations
    CUT: (count: number) => count === 1 ? 'Cut to clipboard' : `${count} items cut`,
    COPIED: (count: number) => count === 1 ? 'Copied to clipboard' : `${count} items copied`,
    PASTED: 'Pasted successfully',
    PASTE_FAILED: 'Failed to paste',
  },

  // Member Messages
  MEMBER: {
    ROLE_UPDATED: 'Role updated',
    ROLE_UPDATED_DESC: (role: string) => `Member role changed to ${role}`,
    ROLE_UPDATE_FAILED: (msg?: string) => msg ?? 'Failed to update role',
    REMOVED: 'Member removed',
    REMOVED_DESC: 'Member has been removed from workspace',
    REMOVE_FAILED: (msg?: string) => msg ?? 'Failed to remove member',
  },

  // Notification Messages
  NOTIFICATION: {
    PREFERENCES_SAVED: 'Preferences saved',
    PREFERENCES_SAVED_DESC: 'Your notification preferences have been updated',
  },

  // Workspace Access Messages (no-access page, workspace selection)
  ACCESS: {
    WORKSPACE_FOUND: 'Workspace found',
    WORKSPACE_FOUND_DESC: 'Redirecting to your workspace...',
    NO_INVITATIONS: 'No invitations found',
    NO_INVITATIONS_DESC: "You don't have any pending workspace invitations.",
    CHECK_FAILED: 'Failed to check for invitations. Please try again.',
    WORKSPACE_CREATED: 'Workspace created',
    WORKSPACE_CREATED_DESC: (name: string) => `Welcome to ${name}!`,
    WORKSPACE_EXISTS: 'Workspace exists',
    WORKSPACE_EXISTS_DESC: 'Redirecting to your workspace...',
    CREATE_FAILED: 'Failed to create workspace. Please try again.',
    SELECT_FAILED: 'Failed to select workspace. Please try again.',
  },

  // Table Operations Messages (type conversion, constraint change, etc.)
  OPERATION: {
    TYPE_CONVERSION_STARTED: 'Type conversion started',
    TYPE_CONVERSION_STARTED_DESC: (fieldName: string, fromType: string, toType: string) =>
      `Converting "${fieldName}" from ${fromType} to ${toType}`,
    TYPE_CONVERSION_COMPLETED: 'Type conversion completed',
    TYPE_CONVERSION_COMPLETED_DESC: (fieldName: string, toType: string) =>
      `"${fieldName}" is now ${toType}`,
    TYPE_CONVERSION_FAILED: (msg?: string) => msg ?? 'Type conversion failed',
    // Constraint change messages
    CONSTRAINT_CHANGE_STARTED: 'Constraint change started',
    CONSTRAINT_CHANGE_STARTED_DESC: (fieldName: string, constraintType: string, toValue: boolean) =>
      `${toValue ? 'Enabling' : 'Disabling'} ${constraintType} constraint on "${fieldName}"`,
    CONSTRAINT_CHANGE_COMPLETED: 'Constraint updated',
    CONSTRAINT_CHANGE_COMPLETED_DESC: (fieldName: string, constraintType: string, toValue: boolean) =>
      `"${fieldName}" is ${toValue ? 'now' : 'no longer'} ${constraintType}`,
    CONSTRAINT_VALIDATION_FAILED: 'Constraint validation failed',
    CONSTRAINT_VALIDATION_FAILED_DESC: (violationCount: number) =>
      `Found ${violationCount} violation${violationCount === 1 ? '' : 's'}. Cannot apply constraint.`,
    CONSTRAINT_CHANGE_FAILED: (msg?: string) => msg ?? 'Constraint change failed',
    // General operation messages
    OPERATION_IN_PROGRESS: 'Operation in progress',
    OPERATION_IN_PROGRESS_DESC: 'Please wait for the current operation to complete',
    OPERATION_CANCELLED: 'Operation cancelled',
    CANCEL_FAILED: (msg?: string) => msg ?? 'Failed to cancel operation',
  },

  // Generic Messages
  GENERIC: {
    OPERATION_FAILED: 'Operation failed',
    UNEXPECTED_ERROR: 'An unexpected error occurred',
    NETWORK_ERROR: 'Network error. Please check your connection.',
    PERMISSION_DENIED: 'You do not have permission to perform this action',
    COPIED: 'Copied to clipboard',
  },
} as const;

/**
 * Type helper for toast message keys
 */
export type ToastMessageKey = keyof typeof ToastMessages;
